import React, { createContext, useContext, useMemo, useCallback, useState, ReactNode } from 'react'

interface GlobalLoadingState {
    isLoading: boolean
    message: string | null
}

interface GlobalLoadingContextType extends GlobalLoadingState {
    startLoading: (message: string) => void
    stopLoading: () => void
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined)

export function GlobalLoadingProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<GlobalLoadingState>({
        isLoading: false,
        message: null
    })

    const startLoading = useCallback((message: string) => {
        setState((prev) => {
            if (prev.isLoading && prev.message === message) return prev
            return { isLoading: true, message }
        })
    }, [])

    const stopLoading = useCallback(() => {
        setState((prev) => {
            if (!prev.isLoading && prev.message == null) return prev
            return { isLoading: false, message: null }
        })
    }, [])

    const value = useMemo<GlobalLoadingContextType>(() => ({
        ...state,
        startLoading,
        stopLoading
    }), [state, startLoading, stopLoading])

    return (
        <GlobalLoadingContext.Provider value={value}>
            {children}
        </GlobalLoadingContext.Provider>
    )
}

export function useGlobalLoading() {
    const context = useContext(GlobalLoadingContext)
    if (context === undefined) {
        throw new Error('useGlobalLoading must be used within a GlobalLoadingProvider')
    }
    return context
}










